import {
  DndContext,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors
} from '@dnd-kit/core';
import {
  SortableContext,
  arrayMove,
  verticalListSortingStrategy
} from '@dnd-kit/sortable';
import { Button, Loading } from '@elotech/components';
import { useEffect, useRef, useState } from 'react';
import Dropzone, { Accept } from 'react-dropzone';

import { Arquivo } from '../../type';
import { DocumentViewerModal, Spinner, alert } from '../';
import { ArquivoEntityWithTemp } from './ArquivoComponent';
import DragDropItem from './DragDropItem';
import styles from './visualizador.module.css';

type Props = {
  service: any;
  files: ArquivoEntityWithTemp[];
  maxSize?: number;
  loading?: boolean;
  checkingSignatures?: boolean;
  canDelete?: boolean | ((file: ArquivoEntityWithTemp) => boolean);
  canView?: boolean | ((file: ArquivoEntityWithTemp) => boolean);
  canDownload?: boolean | ((file: ArquivoEntityWithTemp) => boolean);
  canUpdate?: boolean;
  canUpload?: boolean;
  sizeDivFile?: number;
  acceptedFiles?: Accept;
  maxFileNameLength?: number;
  maxFileCount?: number;
  onAddFile: (file: any) => void;
  onView: (index: number, file: ArquivoEntityWithTemp) => any;
  onDownload: (file: ArquivoEntityWithTemp) => any;
  onRemoveFile: (file: ArquivoEntityWithTemp, index: number) => void;
  onUpdateFile: (file: ArquivoEntityWithTemp, index: number) => void;
  onChangeSigilo: (file: ArquivoEntityWithTemp, index: number) => void;
  extraButton?: (file: ArquivoEntityWithTemp) => any;
  labels?: (file: ArquivoEntityWithTemp) => any;
  onReorder: (files: ArquivoEntityWithTemp[]) => void;
  setFiles: (files: ArquivoEntityWithTemp[]) => void;
  getExtraCaption?(file: ArquivoEntityWithTemp): any;
  canEditHTML: boolean;
  enforceFileSizeLimit?: boolean;
};

const mebi = 1024;
const defaultMessage: string = 'Clique ou arraste arquivos aqui.';
const unidadeMedida = ['Bytes', 'KB', 'MB', 'GB', 'TB', 'PB', 'EB', 'ZB', 'YB'];

const DragDropFiles = (props: Props) => {
  const {
    service,
    files,
    setFiles,
    onView,
    onAddFile,
    onDownload,
    onRemoveFile,
    onUpdateFile,
    onChangeSigilo,
    extraButton,
    labels,
    acceptedFiles,
    loading = false,
    checkingSignatures = false,
    sizeDivFile = 12,
    canDelete = true,
    canView = true,
    canDownload = true,
    canUpdate = true,
    canUpload = true,
    maxSize = 20971520,
    maxFileNameLength,
    onReorder,
    getExtraCaption,
    canEditHTML,
    enforceFileSizeLimit
  } = props;

  const [showViewer, setShowViewer] = useState(false);
  const botaoVisualizarRef = useRef<HTMLButtonElement | null>(null);
  const sensors = useSensors(useSensor(PointerSensor));
  const [errorOnUpload, setErrorOnUpload] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [onDragEnter, setOnDragEnter] = useState(false);

  useEffect(() => {
    const bytes = bytesToSize(maxSize!);

    let errorMessage = 'Somente arquivos com ';

    if (props.maxFileCount) {
      errorMessage = `Somente ${props.maxFileCount} arquivo(s) com `;
    }

    if (maxFileNameLength) {
      errorMessage += `o nome no máximo de ${maxFileNameLength} caracteres, `;
    }

    errorMessage += `tamanho máximo de ${bytes} `;

    if (acceptedFiles) {
      const textAcceptedFiles = Object.values(acceptedFiles).flat().join(' ');
      errorMessage += `e do(s) tipo(s): ${textAcceptedFiles} `;
    }

    setErrorMessage(errorMessage);
  }, []);

  const bytesToSize = (bytes: number): string => {
    if (0 === bytes) return '0 Bytes';

    const menorInteiro = Math.floor(Math.log(bytes) / Math.log(mebi));
    const byteSize = parseFloat(
      (bytes / Math.pow(mebi, menorInteiro)).toFixed(2)
    );

    return `${byteSize}${unidadeMedida[menorInteiro]}`;
  };

  const onDropAccepted = (acceptedFiles: File[]): void => {
    setErrorOnUpload(false);
    setOnDragEnter(false);
    acceptedFiles.forEach(onAddFile);
  };

  const onValidator = (file: File) => {
    if (maxFileNameLength && file.name.length > maxFileNameLength) {
      return {
        code: 'name-too-large',
        message: `O nome do arquivo pode conter no máximo ${maxFileNameLength}`
      };
    }

    return null;
  };

  const onDropRejected = (): void => {
    setErrorOnUpload(true);
    setOnDragEnter(false);
  };

  const onDragEnterEffect = (): void => setOnDragEnter(true);

  const onClickRemoveFile = (file: any, i: number): void => {
    alert({
      type: 'question',
      title: 'Excluir',
      text: `Deseja mesmo excluir esse Arquivo? (${file.arquivo.nome})`,
      onConfirm: () => onRemoveFile(file, i)
    });
  };

  const handleDragEnd = event => {
    const { active, over } = event;

    if (active.id !== over.id) {
      const oldIndex = files.findIndex(file => file.id === active.id);
      const newIndex = files.findIndex(file => file.id === over.id);
      const newFiles = arrayMove(files, oldIndex, newIndex);

      setFiles(newFiles);
      onReorder(newFiles);
    }
  };

  const permite = (
    regra: boolean | ((file: ArquivoEntityWithTemp) => boolean),
    file: ArquivoEntityWithTemp
  ) => (typeof regra === 'function' ? regra(file) : regra);

  const documentosVisualizaveis = files
    ?.filter?.(
      arquivoEntity =>
        !arquivoEntity.temporario && permite(canView, arquivoEntity)
    )
    .map(arquivoEntity => arquivoEntity.arquivo);

  const onDownloadParaViewer = (arquivoParaBaixar: Arquivo) =>
    service
      .gerarUrlParaDownload(
        arquivoParaBaixar.fullPath!,
        arquivoParaBaixar.nome!
      )
      .then(({ data: url }) =>
        service.receberArquivoDoServidor(url.urlAssinada)
      );

  return (
    <div className="dragdrop">
      {canUpload && (
        <Dropzone
          maxSize={enforceFileSizeLimit ? maxSize : undefined}
          accept={acceptedFiles}
          onDropAccepted={onDropAccepted}
          onDropRejected={onDropRejected}
          onDragEnter={onDragEnterEffect}
          validator={onValidator}
          maxFiles={props.maxFileCount}
        >
          {({ getRootProps, getInputProps }) => {
            const rootProps: any = getRootProps?.();
            const inputProps: any = getInputProps?.();
            const mensagemDropzone =
              errorOnUpload && errorMessage ? errorMessage : defaultMessage;

            return (
              <div
                {...rootProps}
                className={`dropzone dropzone--isActive ${
                  errorOnUpload ? 'error' : ''
                }`}
              >
                <div
                  className={`dragdrop-space ${onDragEnter ? 'hover' : ''}`}
                  role="button"
                  aria-label={mensagemDropzone}
                  data-message={mensagemDropzone}
                >
                  <input {...inputProps} aria-label={defaultMessage} />
                </div>
              </div>
            );
          }}
        </Dropzone>
      )}
      {files && files.length > 0 && (
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragEnd={handleDragEnd}
        >
          <SortableContext
            items={files as any}
            strategy={verticalListSortingStrategy}
          >
            <ul>
              {files.map((file: any, i: number) => (
                <DragDropItem
                  file={file}
                  key={file.id}
                  canDelete={permite(canDelete, file)}
                  canView={permite(canView, file)}
                  canDownload={permite(canDownload, file)}
                  canUpdate={canUpdate}
                  sizeDivFile={sizeDivFile}
                  onView={() => onView(i, file)}
                  onDownload={() => onDownload(file)}
                  onRemoveFile={() => onClickRemoveFile(file, i)}
                  onUpdateFile={() => onUpdateFile(file, i)}
                  onChangeSigilo={() => onChangeSigilo(file, i)}
                  canEditHTML={canEditHTML}
                  extraButton={extraButton}
                  labels={labels}
                  getExtraCaption={getExtraCaption}
                  checkingSignature={checkingSignatures}
                />
              ))}
            </ul>
            <ul className={`${styles.rodapeArquivos} ml-xs mb-xs mr-xs`}>
              {documentosVisualizaveis?.length > 0 && (
                <Button
                  type="button"
                  color="neutral"
                  onClick={event => {
                    botaoVisualizarRef.current = event.currentTarget;
                    setShowViewer(true);
                  }}
                  iconPosition="left"
                >
                  <i className="fa fa-eye" />
                  Visualizar documentos
                </Button>
              )}
              {enforceFileSizeLimit && canUpload && (
                <p>(tamanho máximo do arquivo: {bytesToSize(maxSize!)})</p>
              )}
              <p>{`Total de arquivos: ${files?.length}`}</p>
            </ul>
          </SortableContext>
        </DndContext>
      )}
      <Loading contain loading={loading} />
      {loading && <Spinner position="center" className="mb-xs" />}
      {showViewer && (
        <DocumentViewerModal
          onClose={() => setShowViewer(false)}
          returnFocusRef={botaoVisualizarRef}
          documents={documentosVisualizaveis}
          onDownload={onDownloadParaViewer}
          parentIsLoading={loading}
        />
      )}
    </div>
  );
};

export default DragDropFiles;
